import React, {Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import * as actions from '../redux/actions';
import {Row, Col, Card, Anchor} from 'antd';

import './InfoCard.css';
import _ from "lodash";

class InfoCard extends Component {
  render() {

    const dimensions = _.chain(this.props.fields)
      .groupBy('category')
      .map((value, key) => {
        return {category: key, items: value}
      })
      .reduce((reducer, row) => {
        const children = _.map(row.items, f => {
          return (
            <li key={f.field}>
              <b>{f.label}</b>
              <div className="desc">{f.desc}</div>
            </li>
          )
        });

        reducer.push(
          <div key={row.category}>
            <h2 id={_.kebabCase(row.category)}>{row.category}</h2>
            <ul>{children}</ul>
          </div>
        );

        return reducer
      }, [])
      .value();

    return (
      <Card className="InfoCard" noHovering={true} loading={this.props.loading}>
        <Row gutter={32}>
          <Col xs={24} md={20}>
            <h1 id="about">About the Application</h1>

            <p>In 2015, Hawaii spent $7,264 per traditional Medicare beneficiary, but New Jersey spent $11,415. Some
              counties including Miami/Dade actually spent $15,236, double the spend of others including Honolulu county
              whose spend was $7,503. Nationwide, Medicare per capita costs amounted to $9,648. Why the difference?
              Medicare spending, utilization and quality vary widely across states, a phenomenon that is longstanding
              but remains poorly understood. Federal policymakers and health researchers have long recognized that the
              amount and quality of the health care services that Medicare beneficiaries receive vary substantially
              across different regions of the United States. Much of that variation does not appear to be caused by
              differences in beneficiaries’ health, and one widely-publicized estimate asserted that as much as 30
              percent of Medicare expenditures may be unnecessary. </p>

            <p>This application was created to help users start to analyze underlying differences in resource use among
              Medicare beneficiaries in different parts of the country. These fluctuations by geography do not seem to
              reflect beneficiaries' health, the CMS noted in its methodology document for the data, which are released
              annually in what is formally known as the Geographic Public Use File.
              <br/><br/>For more information on Healthcare spend here is a link:
              <br/><a href="https://www.newyorker.com/magazine/2009/06/01/the-cost-conundrum">
                https://www.newyorker.com/magazine/2009/06/01/the-cost-conundrum</a>
            </p>

            <h1 id="data">About the Data</h1>

            <p>This application was developed using public data from the Office of Enterprise Data and Analytics within
              the Centers for Medicare & Medicaid Services (CMS) - <a
                href="http://download.cms.gov/Research-Statistics-Data-and-Systems/Statistics-Trends-and-Reports/Medicare-Geographic-Variation/Downloads/State_County_Table_All.zip">the
                Geographic Variation Public Use File (GV PUF)</a>. This public use file is based primarily on
              information from CMS’s Chronic Conditions Data Warehouse (CCW), which contains 100 percent of Medicare
              claims for beneficiaries who are enrolled in the fee-for-service (FFS) program as well as enrollment and
              eligibility data. The GV PUF covers calendar years 2007-2015 and has information on demographics,
              spending, and service utilization for Medicare beneficiaries in different parts of the country.</p>
            <p>The detailed nature of the CCW claims data makes it possible to analyze differences in cost and/or
              utilization for specific settings of care or types of services. Some of the settings include inpatient
              hospital, outpatient hospital, multiple post-acute care settings (long-term care hospital, inpatient
              rehabilitation facility, skilled nursing facility, and home health agency), hospice, physicians,
              laboratories, and suppliers of durable medical equipment.</p>

            <p>The data also includes physician services which are grouped into six major categories: physician
              evaluation and management, physician procedures, imaging, laboratory tests, durable medical equipment, and
              other. In addition to the measures described above, CMS also calculated the number of times that Medicare
              beneficiaries visited hospital emergency departments and all-cause hospital readmission rates.</p>

            <h2 id="population">Study population</h2>

            <p>This application focuses on Medicare Fee For Service (FFS) beneficiaries. FFS beneficiaries are enrolled
              in both Part A and Part B services.<br/>There is also another group - Medicare Advantage (MA) that also
              covers Part A and B Services. There were 18.9 million beneficiaries in MA plans in 2015,
              about 32 percent of the overall total. MA beneficiary
              counts are included in the application so one can see the population that is not included in FFS but not
              included in the detailed cost and utilization data of the files and the application.</p>
            <p>In summary, this data and application focuses on Medicare Fee For Service (FFS) beneficiaries.
              <br/>Within Medicare FFS beneficiaries there are two types of beneficiaries - those who were under the age
              of 65 and received Medicare because they were either disabled or had end-stage renal disease (5.9 million
              in 2015) and those age 65 and older (nearly 28.2 million in 2015). Both groups are included in the data
              and this application.</p>

            <p>If you want to understand more about this data go to the following Link :
              <br/><a
                href="https://www.cms.gov/Research-Statistics-Data-and-Systems/Statistics-Trends-and-Reports/Medicare-Geographic-Variation/GV_PUF.html">
                https://www.cms.gov/Research-Statistics-Data-and-Systems/Statistics-Trends-and-Reports/Medicare-Geographic-Variation/GV_PUF.html </a>
              and research the State/County Table - All Beneficiaries.</p>

            <h2 id="data-prep">Data Preparation‌</h2>

            <p>This data and application will help users analyze underlying differences in resource use among Medicare
              beneficiaries in different parts of the country. However, Medicare spending and utilization can vary for
              reasons that are not attributable to practice patterns or willingness to seek care, and two of those
              reasons are particularly important. First, Medicare often pays different amounts for the same service in
              different areas (for example, to reflect variation in local wages or input prices). Second, the health of
              Medicare beneficiaries also varies geographically, and those differences will clearly affect spending and
              utilization.</p>
            <p>To account for those factors, CMS modified the data from the CCW in two ways:
              <br/><b>Standardization</b> -
              CMS standardized Medicare’s payment amounts to remove geographic differences in payment
              rates for individual services as a source of variation.
              <br/><b>Risk Adjustment</b> -
              CMS adjusted for differences in beneficiaries’ health using the risk-adjustment model
              that CMS uses to pay MA plans.
            </p>

            <h2 id="standardization">Standardization</h2>

            <p>CMS standardized payment rates using the same methodology that CMS uses to calculate its Medicare
              spending per beneficiary (MSPB) metric for the hospital value-based purchasing program. (The only
              exception is that CMS standardize the Medicare payment amount, while the methodology used for the MSPB
              standardizes the allowed amount.) This methodology examines Medicare’s various FFS payment systems and
              identifies the factors that lead to different payment rates for the same service. In general, those
              factors are adjustments that Medicare makes to account for local wages or input prices, and extra payments
              that Medicare makes to advance other program goals, such as compensating certain hospitals for the cost of
              training doctors. CMS generally then either built up a payment amount using just the base rate for the
              service and the weight applied under the particular payment system for that service, or worked backward
              from the actual payment amount to determine what Medicare would have paid without those adjustments. More
              details are provided in the metric descriptions or on the CMS website <a
                href="https://www.cms.gov/Research-Statistics-Data-and-Systems/Statistics-Trends-and-Reports/Medicare-Geographic-Variation/Downloads/Geo_Var_PUF_Methods_Paper.pdf">
                here</a>
            </p>

            <h2 id="risk-adjustment">Risk Adjustment</h2>

            <p>CMS developed a risk-adjustment model that uses HCCs (hierarchical condition categories) to assign risk
              scores. Those scores estimate how beneficiaries’ FFS spending will compare to the overall average for the
              entire Medicare population. The average risk score is set at 1.0; beneficiaries with scores greater than
              that are expected to have above-average spending, and vice versa. Risk scores are based on a beneficiary’s
              age and sex; whether the beneficiary is eligible for Medicaid, first qualified for Medicare on the basis
              of disability, or lives in an institution (usually a nursing home); and the beneficiary’s diagnoses from
              the previous year. The HCC model was designed for risk adjustment on larger populations, such as the
              enrollees in an MA plan, and generates more accurate results when used to compare groups of beneficiaries
              rather than individuals.</p>

            <p>CMS used the risk scores to adjust spending data at the beneficiary level rather than in aggregate. As a
              result, the aggregate standardized, risk-adjusted spending in a region does not equal the aggregate
              standardized costs divided by the average HCC risk score. In addition, the HCC model was not designed to
              risk-adjust spending for individual services and therefore is not applied to service-level spending.</p>

            <p>By standardizing payment amounts and adjusting for differences in beneficiaries’ health status, the data
              provides a more accurate picture of how resource use varies for Medicare beneficiaries across the
              country.</p>


            <h1 id="metrics">Metrics</h1>

            {dimensions}

          </Col>
          <Col xs={24} md={4}>
            <Anchor affix={false}>
              <Anchor.Link href="#about" title="About The Application"/>
              <Anchor.Link href="#data" title="About The Data">
                <Anchor.Link href="#population" title="Study Population"/>
                <Anchor.Link href="#data-prep" title="Data Preparation"/>
                <Anchor.Link href="#standardization" title="Standardization"/>
                <Anchor.Link href="#risk-adjustment" title="Risk Adjustment"/>
              </Anchor.Link>
              <Anchor.Link href="#metrics" title="Metrics">
                <Anchor.Link href="#total-costs" title="Total Costs"/>
                <Anchor.Link href="#readmissions" title="Readmissions"/>
                <Anchor.Link href="#service-level-costs" title="Service Costs"/>
                <Anchor.Link href="#service-level-utilization" title="Service Utilization"/>
                <Anchor.Link href="#demographics" title="Demographics"/>
              </Anchor.Link>
            </Anchor>
          </Col>
        </Row>
      </Card>
    );
  }
}


function mapStoreToProps(store, props) {
  return {
    loading: store.states.loading || store.dimensions.loading,
    fields: store.dimensions.fields
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

export default connect(mapStoreToProps, mapDispatchToProps)(InfoCard);


// WEBPACK FOOTER //
// ./src/containers/InfoCard.js